<template>
  <WdsStack class="tw-bg-true-gray-50 tw-p-[24px] tw-rounded-[4px] tw-relative tw-z-0">
    <div class="tw-grow">
      <WdsStack
        align-center
        justify-space-between
        :class="{
          'tw-mb-4':
            opened ||
            !latestState ||
            latestState.state === ApprovalApprovableState.Requested ||
            latestState?.state === ApprovalApprovableState.Rejected,
        }"
      >
        <WdsStack gap-sm align-start>
          <WdsStack gap-xs align-start direction-column>
            <WdsText xl>
              {{ approvable.approval.name }}
            </WdsText>
            <WdsText small>
              <WdsTag sm>{{ getApprovalTriggerLabel(approvable.approval.trigger) }}</WdsTag>
            </WdsText>
          </WdsStack>

          <WdsTag
            md
            :success="latestState?.state === ApprovalApprovableState.Approved"
            :error="
              latestState?.state === ApprovalApprovableState.Rejected ||
              latestState?.state === ApprovalApprovableState.Cancelled
            "
            :warning="!latestState || latestState.state === ApprovalApprovableState.Requested"
            class="!tw-rounded tw-mt-1"
            :class="{
              '!tw-bg-[#11A151] !tw-text-white':
                latestState?.state === ApprovalApprovableState.Approved,
              '!tw-bg-[#EB2E2E] !tw-text-white':
                latestState?.state === ApprovalApprovableState.Rejected ||
                latestState?.state === ApprovalApprovableState.Cancelled,
              '!tw-bg-[#FD8A33] !tw-text-white':
                !latestState || latestState.state === ApprovalApprovableState.Requested,
            }"
            data-test="approval-request-status"
          >
            {{ status }}
          </WdsTag>
        </WdsStack>

        <WdsStack gap-md class="tw-items-center">
          <div v-if="steps.length > 0">
            <WdsTag md gray class="!tw-bg-gray-100 !tw-rounded-full !tw-text-gray-800">
              {{
                $tc(':number step | :number steps', countApprovalSteps, {
                  number: countApprovalSteps,
                })
              }}
            </WdsTag>
          </div>
          <WdsButtonIcon sm text-left transparent-background @click.prevent="opened = !opened">
            <template #text>
              <span class="tw-text-base">
                {{ opened ? $t('Collapse') : $t('See all') }}
              </span>
            </template>
            <WdsIconChevronDown
              :class="[
                'tw-transition-transform tw-duration-300 tw-transform tw-w-4 tw-h-4',
                opened ? 'tw-rotate-180' : '',
              ]"
            />
          </WdsButtonIcon>
        </WdsStack>
      </WdsStack>

      <WdsCollapse v-model:open="opened" content-id="approval-progress">
        <template #title>
          <span></span>
        </template>
        <template #content>
          <WdsProgress
            v-if="steps.length > 0"
            :steps="steps"
            :current="currentStep"
            :most-completed-step="mostCompletedStep"
            :failed-steps="failedSteps"
            secondary
            sm
            class="tw-w-full !tw-block"
          >
            <template #parent="{ mStep }">
              <ApprovalProgressStep
                :m-step="mStep"
                :viewer-can-action="approvable.viewerCanAction"
                @show-approval-request-modal="showApprovalRequestModal = true"
              />
            </template>
          </WdsProgress>
        </template>
      </WdsCollapse>

      <WdsProgress
        v-if="
          !opened &&
          steps.length > 0 &&
          latestState &&
          latestState.state === ApprovalApprovableState.Requested
        "
        :steps="[currentStepObject]"
        :current="[0]"
        secondary
        sm
        class="tw-w-full !tw-block"
      >
        <template #parent="{ mStep }">
          <ApprovalProgressStep
            :m-step="mStep"
            :viewer-can-action="approvable.viewerCanAction"
            @show-approval-request-modal="showApprovalRequestModal = true"
          />
        </template>
      </WdsProgress>

      <WdsProgress
        v-if="
          !opened &&
          steps.length > 0 &&
          latestState &&
          latestState.state === ApprovalApprovableState.Rejected
        "
        :steps="[rejectedStepObject]"
        :failed-steps="[0]"
        :most-completed-step="[1]"
        :current="[1]"
        secondary
        sm
        class="tw-w-full !tw-block"
      >
        <template #parent="{ mStep }">
          <ApprovalProgressStep
            :m-step="mStep"
            @show-approval-request-modal="showApprovalRequestModal = true"
          />
        </template>
      </WdsProgress>
    </div>
  </WdsStack>

  <Teleport to="body">
    <ApprovalRequestModal
      v-model:show="showApprovalRequestModal"
      :approval-approvable-id="approvable.id"
      @refresh-approvables="refreshApprovalRequests"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue'
import {
  WdsTag,
  WdsText,
  WdsStack,
  WdsProgress,
  WdsCollapse,
  WdsButtonIcon,
  WdsIconChevronDown,
} from '@worksome/wds-components'
import useDate from '@/composables/useDate'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import type { Approvable } from '@/components/Workflows/Approvals/approvals-types'
import { ApprovalApprovableState, ApprovalTrigger } from '@/gql/graphql'
import ApprovalRequestModal from '@/components/Workflows/Approvals/Request/ApprovalRequestModal.vue'
import ApprovalProgressStep from '@/components/Workflows/Approvals/Request/ApprovalProgressStep.vue'
import { getApprovalStateStatusLabel } from '@/components/Workflows/Approvals/utilities/statusFormatting'

const props = defineProps({
  approvable: {
    type: Object as PropType<Approvable>,
    required: true,
  },
  hideTalent: {
    type: Boolean,
    default: false,
  },
  progressListOnly: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['reload-approvals'])

const approvers = computed(() => props.approvable?.approvalRule?.approvers || [])

const showApprovalRequestModal = ref(false)

const opened = ref(false)

const getApprovalTriggerLabel = (trigger: string) => {
  switch (trigger) {
    case ApprovalTrigger.HireCreated:
      return $t('Hire Created')
    case ApprovalTrigger.HireChanged:
      return $t('Hire Changed')
    case ApprovalTrigger.ClassificationCreated:
      return $t('Classification Created')
    default:
      return $t('Unknown')
  }
}

const requestedBy = computed(() =>
  props.approvable?.approvalStates?.find(
    (state) => state.state === ApprovalApprovableState.Requested,
  ),
)

const latestState = computed(() => {
  if (props.approvable?.approvalStates?.length > 0) {
    return props.approvable.approvalStates[props.approvable.approvalStates.length - 1]
  }

  return null
})

const status = computed(() => {
  if (!latestState.value) {
    return $t('Pending')
  }

  return getApprovalStateStatusLabel(latestState.value.state)
})

const steps = computed(() => {
  const results: any = []

  if (requestedBy.value) {
    results.push({
      name: $t('Approval requested'),
      useTagForName: false,
      url: '',
      isCurrent: false,
      params: {
        user: requestedBy.value.actionedBy,
        date: useDate(requestedBy.value.createdAt, 'MMM DD, YYYY').date.value,
        upComing: false,
      },
    })
  }

  if (approvers.value.length > 0) {
    approvers.value.forEach((approver: any) => {
      const approverLatestState =
        props.approvable?.approvalStates
          ?.filter((state) => state.approver.id === approver.id)
          ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ||
        null

      const isCurrent: boolean = approverLatestState?.state === ApprovalApprovableState.Requested
      const upComing: boolean = approverLatestState === null

      const user = !upComing && !isCurrent ? (approverLatestState?.actionedBy ?? null) : null
      const date = approverLatestState
        ? useDate(approverLatestState.createdAt, 'MMM DD, YYYY').date.value
        : null
      const stepStatus = approverLatestState?.state
        ? {
            state: approverLatestState.state,
            label: getApprovalStateStatusLabel(approverLatestState.state),
          }
        : null
      const message = approverLatestState?.message ?? null

      results.push({
        name: approver.userGroup.name,
        useTagForName: true,
        url: '',
        isCurrent,
        params: {
          user,
          date,
          status: stepStatus,
          message,
          userGroup: approver.userGroup,
          upComing,
        },
      })
    })
  }

  return results
})

const countApprovalSteps = computed(
  () => steps.value.filter((step: { params: { userGroup: any } }) => step.params.userGroup).length,
)

const currentStep = computed(() => {
  const theCurrentStep = steps.value.findIndex((step: any) => step.isCurrent)

  if (theCurrentStep === -1) {
    return []
  }

  return [theCurrentStep]
})

const currentStepObject = computed(() => {
  const theCurrentStep = steps.value.findIndex((step: any) => step.isCurrent)

  if (theCurrentStep !== -1) {
    return steps.value[theCurrentStep]
  }

  return null
})

const rejectedStepObject = computed(() => {
  const theRejectedStep = steps.value.findIndex(
    (step: any) => step.params.status?.state === ApprovalApprovableState.Rejected,
  )

  if (theRejectedStep !== -1) {
    return steps.value[theRejectedStep]
  }

  return null
})

const mostCompletedStep = computed(() => {
  const theCurrentStep = steps.value.findIndex((step: any) => step.isCurrent)

  if (theCurrentStep !== -1) {
    return [theCurrentStep - 1]
  }

  if (latestState.value?.state === ApprovalApprovableState.Approved) {
    return [steps.value.length - 1]
  }

  return [0]
})

const failedSteps = computed(() => {
  if (
    latestState.value?.state === ApprovalApprovableState.Rejected ||
    latestState.value?.state === ApprovalApprovableState.Cancelled
  ) {
    return [
      steps.value?.findIndex(
        (step: any) =>
          step.params.status?.state === ApprovalApprovableState.Rejected ||
          step.params.status?.state === ApprovalApprovableState.Cancelled,
      ),
    ]
  }

  return []
})

function refreshApprovalRequests() {
  emit('reload-approvals')
}
</script>
